import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Pagination,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import ReportFilters, { today, monthAgo } from "./ReportFilters";
import { formatShowDateTimeIST } from "../../utils/dateTimeIST";
import toast from "react-hot-toast";

const fmt = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const CancellationsReport: React.FC = () => {
  const [filters, setFilters] = useState({
    dateFrom: monthAgo,
    dateTo: today,
    cinemaId: "",
    cancelledBy: "",
    category: "",
    paymentMethod: "",
  });
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cinemas, setCinemas] = useState<Array<{ id: string; name: string }>>(
    [],
  );

  const limit = 50;

  useEffect(() => {
    adminService
      .getCinemas()
      .then((r: any) => {
        const list = r?.data || [];
        setCinemas(
          list.map((c: any) => ({ id: c.vistacinemaid || c.id, name: c.name })),
        );
      })
      .catch(() => setCinemas([]));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await adminService.getCancellationsReport({
        ...filters,
        page,
        limit,
      });
      setRows(r?.data || []);
      setTotal(r?.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || "Failed to load cancellations report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onApply = () => {
    setPage(1);
    fetchData();
  };

  const onReset = () => {
    setFilters({
      dateFrom: monthAgo,
      dateTo: today,
      cinemaId: "",
      cancelledBy: "",
      category: "",
      paymentMethod: "",
    });
    setPage(1);
    setTimeout(fetchData, 0);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await adminService.exportCancellationsReport(filters);
      toast.success("Excel downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Helmet>
        <title>Cancellations Report - MyCinemas Admin</title>
      </Helmet>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Cancellations Report</h2>
          <small className="text-muted">
            One row per cancelled booking with charge breakdown. Yellow rows =
            admin-cancelled (full refund).
          </small>
        </div>
        <Badge bg="warning" text="dark" className="p-2">
          {total.toLocaleString("en-IN")} total
        </Badge>
      </div>

      <ReportFilters
        {...filters}
        cinemas={cinemas}
        onChange={(p) => setFilters((prev) => ({ ...prev, ...p }))}
        onApply={onApply}
        onReset={onReset}
        onExport={onExport}
        loading={loading}
        exporting={exporting}
        showPaymentMethod
        showCancelledBy
        showCategory
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Bkg ID</th>
                <th>Cancelled At</th>
                <th>Cinema</th>
                <th>Movie</th>
                <th>Tickets</th>
                <th className="text-end">Ticket Total</th>
                <th className="text-end">Conv. Fee</th>
                <th>Cancel %</th>
                <th className="text-end">Cancel Charge</th>
                <th className="text-end">Refund Amount</th>
                <th>Category</th>
                <th>Payment Method</th>
                <th>Cancelled By</th>
                <th>Gateway</th>
                <th className="text-end">Gateway Fee</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="text-center py-4">
                    <Spinner size="sm" /> Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="text-center py-4 text-muted">
                    No cancellations found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: any) => (
                  <tr
                    key={r.bookingId}
                    style={
                      r.cancelledBy === "admin"
                        ? { backgroundColor: "#FFF8DC" }
                        : undefined
                    }
                  >
                    <td className="font-monospace small">{r.bookingId}</td>
                    <td className="small">{formatShowDateTimeIST(r.cancelledAt)}</td>
                    <td className="small">{r.cinema}</td>
                    <td className="small">{r.movie}</td>
                    <td className="small">{r.tickets}</td>
                    <td className="text-end small">{fmt(r.ticketTotal)}</td>
                    <td className="text-end small">{fmt(r.convFee)}</td>
                    <td className="small">{r.cancelPercent}%</td>
                    <td className="text-end small">{fmt(r.cancelCharge)}</td>
                    <td className="text-end small fw-bold">
                      {fmt(r.refundAmount)}
                    </td>
                    <td className="small">
                      {r.cancellationCategory === "my_cinemas_issue" ? (
                        <Badge bg="info">MY Cinemas</Badge>
                      ) : r.cancellationCategory === "customer_initiated" ? (
                        <Badge bg="secondary">Customer</Badge>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="small">{r.paymentMethod || "-"}</td>
                    <td className="small">
                      <Badge
                        bg={r.cancelledBy === "admin" ? "warning" : "light"}
                        text={r.cancelledBy === "admin" ? "dark" : "dark"}
                      >
                        {r.cancelledBy}
                      </Badge>
                    </td>
                    <td className="small">{r.gateway || "-"}</td>
                    <td className="text-end small">{fmt(r.gatewayFee)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              disabled={page === 1}
              onClick={() => setPage(1)}
            />
            <Pagination.Prev
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
            <Pagination.Last
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </>
  );
};

export default CancellationsReport;
